import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Alert, Input, Modal, Tag } from 'antd';
import CheckAccess from 'navigation/CheckAccess';
import BWButton from 'components/shared/BWButton';
import { changePromotionLifecycle } from 'services/promotions.service';
import { showToast } from 'utils/helpers';

export const lifecycleColors = { active: 'green', paused: 'orange', expired: 'red', stopped: 'default', scheduled: 'blue' };
export const LifecycleBadge = ({ promotion = {} }) => (
  <Tag color={lifecycleColors[promotion.lifecycle_status]}>{promotion.lifecycle_label || 'Chưa xác định'}</Tag>
);

const actionLabels = { pause: 'Tạm dừng', resume: 'Tiếp tục áp dụng', stop: 'Dừng chương trình' };
const PromotionLifecycle = ({ loadDetail }) => {
  const { watch } = useFormContext();
  const promotion = watch();
  const [action, setAction] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (!promotion.promotion_id) return null;
  const actionable = ['active', 'scheduled', 'paused'].includes(promotion.lifecycle_status)
    && Number(promotion.is_review) === 1 && Number(promotion.is_active) === 1;
  const openAction = (value) => { setAction(value); setReason(''); setError(''); };
  const submit = async () => {
    if (busy) return;
    if (!reason.trim()) { setError('Vui lòng nhập lý do.'); return; }
    setBusy(true);
    try {
      await changePromotionLifecycle(promotion.promotion_id, {
        action, reason: reason.trim(), lifecycle_version: promotion.lifecycle_version,
      });
      showToast.success(`${actionLabels[action]} thành công.`);
      setAction(null);
      await loadDetail();
    } catch (e) { setError(e.message || 'Không thể cập nhật trạng thái.'); }
    finally { setBusy(false); }
  };
  return (
    <section className='bw_frm_box' aria-label='Hiệu lực chương trình'>
      <div className='bw_row bw_align_items_center'>
        <div className='bw_col_7'>
          <h3>Hiệu lực chương trình</h3>
          <LifecycleBadge promotion={promotion} />
          <p>Áp dụng từ {promotion.begin_date} đến hết ngày {promotion.end_date || 'không giới hạn'}.</p>
          {promotion.lifecycle_status === 'expired' && <p>Đã hết thời gian áp dụng. Chương trình không còn được chọn cho đơn mới.</p>}
          {promotion.lifecycle_status === 'paused' && <p>Đang tạm dừng áp dụng cho đơn mới. Có thể tiếp tục khi chương trình còn hạn.</p>}
        </div>
        <div className='bw_col_5 bw_flex bw_justify_content_right' style={{ gap: 8, flexWrap: 'wrap' }}>
          {actionable && <CheckAccess permission='PROMOTION_EDIT'>
            <BWButton type={promotion.is_paused ? 'success' : 'warning'}
              icon={promotion.is_paused ? 'fa fa-play' : 'fa fa-pause'}
              content={promotion.is_paused ? actionLabels.resume : actionLabels.pause}
              onClick={() => openAction(promotion.is_paused ? 'resume' : 'pause')} />
            <BWButton type='danger' outline icon='fa fa-stop' content={actionLabels.stop} onClick={() => openAction('stop')} />
          </CheckAccess>}
        </div>
      </div>
      {promotion.lifecycle_history?.length > 0 && <details style={{ marginTop: 12 }}>
        <summary>Lịch sử thay đổi trạng thái</summary>
        <ul style={{ paddingLeft: 20, marginTop: 8 }}>
          {promotion.lifecycle_history.map((item, index) => <li key={`${item.created_date}-${index}`} style={{ marginBottom: 8 }}>
            <b>{actionLabels[item.action] || item.action}</b> · {item.created_date} · {item.created_user}<br />{item.reason}
          </li>)}
        </ul>
      </details>}
      <Modal open={Boolean(action)} title={actionLabels[action]} onCancel={() => !busy && setAction(null)}
        onOk={submit} okText={actionLabels[action]} cancelText='Quay lại' confirmLoading={busy}
        cancelButtonProps={{ disabled: busy }} closable={!busy} maskClosable={!busy}>
        <p>{action === 'stop' ? 'Sau khi dừng, chương trình không thể tiếp tục áp dụng.'
          : action === 'pause' ? 'Tạm ngừng áp dụng cho đơn mới. Bạn có thể tiếp tục khi chương trình còn hạn.'
          : 'Cho phép áp dụng lại theo thời gian và điều kiện đã được duyệt.'}</p>
        <p>Các ưu đãi đã lưu trên đơn hàng được giữ lại.</p>
        <label htmlFor='promotion-lifecycle-reason'>Lý do <span aria-hidden='true'>*</span></label>
        <Input.TextArea id='promotion-lifecycle-reason' value={reason} onChange={(e) => setReason(e.target.value)}
          maxLength={1000} rows={3} disabled={busy} placeholder='Nhập lý do thay đổi trạng thái' />
        {error && <Alert type='error' message={error} showIcon style={{ marginTop: 12 }} />}
      </Modal>
    </section>
  );
};
export default PromotionLifecycle;
